import { Injectable } from '@angular/core';

import { OptionsOrdering } from '../../common/types';
import { extractSizeValue } from '../../common/utils/extract-size-value';

type NamedItem = { name: string };

/**
 * Sorts options according to the configured ordering.
 */
@Injectable({
    providedIn: 'root',
})
export class OptionsOrderingService {
    sort<T extends NamedItem>(options: T[], ordering: OptionsOrdering): T[] {
        switch (ordering) {
            case 'default':
                return options
                    .slice()
                    .sort((a, b) => (extractSizeValue(a.name) < extractSizeValue(b.name) ? -1 : 1));
            case 'alphabetical':
                return options.slice().sort((a, b) => (a.name < b.name ? -1 : 1));
            case 'dimension':
                return options
                    .slice()
                    .sort((a, b) =>
                        this.extractDimensionValue(a.name) < this.extractDimensionValue(b.name) ? -1 : 1,
                    );
            case 'brush-size':
                return options.slice().sort((a, b) => this.sortByBrushSize(a, b));
        }
    }

    private sortByBrushSize(a: NamedItem, b: NamedItem) {
        const aScore = this.getBrushZeroScore(a.name) ?? +a.name;
        const bScore = this.getBrushZeroScore(b.name) ?? +b.name;

        return aScore < bScore ? -1 : 1;
    }

    private getBrushZeroScore(name: string) {
        if (/^0+$/.test(name)) {
            return name.length * -1;
        }
        return;
    }

    private extractDimensionValue(name: string): number {
        const aSeriesRe = /^A(\d)/;
        const aSeriesmatches = name.match(aSeriesRe);
        if (aSeriesmatches?.length === 2) {
            return 1 / Number.parseInt(aSeriesmatches[1]);
        }

        const mmRe = /([\d.]+)\s?mm/gi;
        const cmRe = /([\d.]+)\s?cm/gi;
        const inchRe = /([\d/ ]+)"/gi;

        let value = 0;
        for (const regex of [mmRe, cmRe, inchRe]) {
            const matches = name.match(regex);
            for (const match of matches ?? []) {
                value += Number.parseInt(match) || 0;
            }
        }
        return value;
    }
}
